import * as React from 'react';

import MotionOpacity from '@components/motion/motion-opacity';
import MerchProductDisplayCtrl from '../merch-product-display';
import styled from 'styled-components';
import { ListProductResponse } from '@services/merch/types/api/list-product';
import { CallHistoryMethodAction, push } from 'connected-react-router';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';

import { MerchProduct } from '@services/merch/types/api/merch-product';
import { SortableContainer, SortEnd } from 'react-sortable-hoc';
import {
  DeleteMerch,
  UpdateMerchOrder,
} from '@webapp/store/merch/action-creators';
import arrayMove from 'array-move';
import AppState from '@webapp/store/state';
import { OwnershipState } from '@webapp/store/ownership/state';
import { getOwnershipTotal } from '@webapp/store/ownership/selectors';

interface MerchProductDisplayListCtrlProps {
  className?: string;
  artistId?: string;
  products: ListProductResponse['items'];
  goToEdit: (id: string) => CallHistoryMethodAction;
  handleTakeDown: (product: MerchProduct) => void;
  handlePublish: (product: MerchProduct) => void;
  updateMerchOrder: (artistId: string, newOrder: string[]) => UpdateMerchOrder;
  ownershipState: OwnershipState;
  takeDown: (artistId: string, productId: string) => DeleteMerch;
}

const mapStateToProps = (state: AppState) => ({
  artistId: state.artist.active?.id,
  ownershipState: state.ownership,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  goToEdit: (id: string) => dispatch(push(`/merch/edit/${id}`)),
  updateMerchOrder: (artistId: string, newOrder: string[]) =>
    dispatch(new UpdateMerchOrder(artistId, newOrder)),
});

const MAX_LENGTH = 35;

const SortableMerchProductDisplayListCtrl = SortableContainer<MerchProductDisplayListCtrlProps>(
  ({
    className,
    products,
    goToEdit,
    handleTakeDown,
    handlePublish,
    ownershipState,
    artistId,
    takeDown,
  }: MerchProductDisplayListCtrlProps) => {
    const canPublishMerch = (product: MerchProduct): boolean => {
      return (
        product.status === 'draft' &&
        product.price >= product.cost &&
        getOwnershipTotal(ownershipState, product.id, 'OWNER') === 100
      );
    };

    return (
      <MotionOpacity className={className}>
        <ProductListWrapper>
          {products.map((product, index) => (
            <MerchProductDisplayCtrl
              key={`${product.title}-${index}`}
              index={index}
              title={product.title}
              text={`${product.description.slice(0, MAX_LENGTH)}${
                product.description.length > MAX_LENGTH ? '...' : ''
              }`}
              itemImg={product.image ? product.image : ''}
              price={`${product.price}`}
              productionCost={`${product.cost}`}
              disablePublish={!canPublishMerch(product)}
              onSelect={(value: string) => {
                if (value === 'edit') {
                  goToEdit(product.id);
                } else if (value === 'publish') {
                  handlePublish(product);
                } else if (value === 'takedown') {
                  takeDown(artistId ?? '', product.id);
                }
              }}
              onTakeDown={() => handleTakeDown(product)}
              status={product.status}
            />
          ))}
        </ProductListWrapper>
      </MotionOpacity>
    );
  }
);

const MerchProductDisplayListCtrl = (
  props: MerchProductDisplayListCtrlProps
) => {
  const updateProductsOrder = ({ oldIndex, newIndex }: SortEnd) => {
    if (props.artistId && newIndex !== oldIndex) {
      let newOrder = arrayMove(props.products, oldIndex, newIndex);
      props.updateMerchOrder(
        props.artistId,
        newOrder.map((it) => it.id)
      );
    }
  };

  return (
    <SortableMerchProductDisplayListCtrl
      {...props}
      onSortEnd={(newSort) => updateProductsOrder(newSort)}
      useDragHandle
      lockAxis="y"
      lockToContainerEdges
      helperClass="sorting"
    />
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(MerchProductDisplayListCtrl);

const ProductListWrapper = styled.div`
  & > div {
    margin-bottom: 10px;

    &:last-child {
      margin-bottom: 0;
    }
  }
`;
